import { useEffect, useMemo, useState } from 'react';

export interface GlobalCustomInputLike {
  id?: string;
  title?: string;
  label?: string;
  value?: string;
  options?: string[];
}

interface UseGlobalCustomInputsParams<T extends GlobalCustomInputLike> {
  globalCustomInputs?: T[];
  merchantGlobalCustomInputs?: T[];
  isEditMode: boolean;
  setGlobalCustomInputs: (updater: (currentInputs: T[]) => T[]) => void;
}

export const getGlobalCustomInputKey = <T extends GlobalCustomInputLike>(
  input: T,
  index: number,
): string => input.id || input.title || input.label || `global-custom-${index}`;

export function useGlobalCustomInputs<T extends GlobalCustomInputLike>({
  globalCustomInputs,
  merchantGlobalCustomInputs,
  isEditMode,
  setGlobalCustomInputs,
}: UseGlobalCustomInputsParams<T>) {
  const displayedGlobalCustomInputs = useMemo(
    () =>
      globalCustomInputs && globalCustomInputs.length > 0
        ? globalCustomInputs
        : isEditMode
          ? []
          : (merchantGlobalCustomInputs ?? []),
    [globalCustomInputs, isEditMode, merchantGlobalCustomInputs],
  );

  const [localCustomInputValues, setLocalCustomInputValues] = useState<Record<string, string>>({});

  useEffect(() => {
    setLocalCustomInputValues((currentValues) => {
      let hasChanges = false;
      const nextValues = { ...currentValues };

      displayedGlobalCustomInputs.forEach((input, inputIndex) => {
        const inputKey = getGlobalCustomInputKey(input, inputIndex);
        if (nextValues[inputKey] === undefined) {
          nextValues[inputKey] = globalCustomInputs?.[inputIndex]?.value ?? input.value ?? '';
          hasChanges = true;
        }
      });

      return hasChanges ? nextValues : currentValues;
    });
  }, [displayedGlobalCustomInputs, globalCustomInputs]);

  const getGlobalCustomInputValue = (input: T, inputIndex: number): string => {
    const inputKey = getGlobalCustomInputKey(input, inputIndex);
    return localCustomInputValues[inputKey] ?? globalCustomInputs?.[inputIndex]?.value ?? input.value ?? '';
  };

  const updateGlobalCustomInputValue = (inputIndex: number, sourceInput: T, value: string) => {
    const sourceKey = getGlobalCustomInputKey(sourceInput, inputIndex);
    setLocalCustomInputValues((currentValues) => ({
      ...currentValues,
      [sourceKey]: value,
    }));

    setGlobalCustomInputs((currentInputs) => {
      const nextInputs = (currentInputs ?? []).filter(Boolean);
      const existingIndex = nextInputs.findIndex(
        (input, idx) => getGlobalCustomInputKey(input, idx) === sourceKey,
      );
      const nextInput = {
        ...sourceInput,
        ...(existingIndex >= 0 ? nextInputs[existingIndex] : {}),
        value,
        title: sourceInput.title || sourceInput.label || '',
        options: sourceInput.options || [],
      } as T;

      if (existingIndex >= 0) {
        nextInputs[existingIndex] = nextInput;
      } else {
        nextInputs.push(nextInput);
      }

      return nextInputs;
    });
  };

  return {
    displayedGlobalCustomInputs,
    getGlobalCustomInputValue,
    updateGlobalCustomInputValue,
  };
}
